import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Typography, Tabs, Tab, Button, Grid, Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { GET_PET_PHOTOS } from '../../queries';
import { useHubPetParent } from './HubPetParentContext';
import { PetPhotosResponse } from '../../types/PetPhoto';
import HubSectionContainer from './CustomContainer';
import PhotoCard from './PhotoCard';
import MediaDialog from '../MediaDialog';
import PhotoLoadingGrid from './PhotoLoadingGrid';
import PhotoEmptyState from './PhotoEmptyState';
import usePhotoValidation from './usePhotoValidation';
import useMediaDialog from './useMediaDialog';
import { PHOTOS_PER_PAGE } from './photoUtils';
import { track } from './analytics';
import useHubPageView from './useHubPageView';
import { photoPetLabel } from './petLabel';

// Fosters share photos to Community alongside adopters.
const COMMUNITY_CARE_TYPES = ['Adopted', 'Foster'];

function a11yProps(index: number) {
  return {
    id: `community-tab-${index}`,
    'aria-controls': `community-tabpanel-${index}`,
  };
}

interface AwoTab {
  awoId: string;
  awoName: string;
}

function CommunityMoments() {
  useHubPageView();
  const [activeTab, setActiveTab] = useState(0);
  const { dialogState, openMediaDialog, setDialogOpen } = useMediaDialog();

  const { petParent, loading: userLoading } = useHubPetParent();

  // Get distinct AWOs from pet parent's animals
  const awoTabs = useMemo<AwoTab[]>(() => {
    if (!petParent?.animals) return [];

    const awoMap = new Map<string, string>();
    petParent.animals.forEach((animal) => {
      if (animal.awoId && (animal.awo?.displayName ?? animal.awo?.name)) {
        awoMap.set(animal.awoId, animal.awo?.displayName ?? animal.awo?.name ?? '');
      }
    });

    const shelterTabs = Array.from(awoMap.entries()).map(([awoId, awoName]) => ({
      awoId,
      awoName,
    }));

    // Prepend "All Shelters" tab
    return [
      { awoId: '', awoName: 'All Shelters' }, // Empty string for awoId will be converted to null in query
      ...shelterTabs,
    ];
  }, [petParent?.animals]);

  // Get awoId for the active tab (null for "All Shelters", specific ID for others)
  const awoIdForQuery = activeTab === 0 ? null : awoTabs[activeTab]?.awoId;

  // Reset active tab if it's out of bounds
  useEffect(() => {
    if (activeTab >= awoTabs.length && awoTabs.length > 0) {
      setActiveTab(0);
    }
  }, [activeTab, awoTabs.length]);

  const {
    data,
    loading: photosLoading,
    fetchMore,
  } = useQuery<{ petPhotos: PetPhotosResponse }>(GET_PET_PHOTOS, {
    variables: {
      awoId: awoIdForQuery ? parseInt(awoIdForQuery, 10) : null,
      careTypes: COMMUNITY_CARE_TYPES,
      limit: PHOTOS_PER_PAGE,
      offset: 0,
    },
    skip: awoTabs.length === 0,
    fetchPolicy: 'cache-and-network', // Show cached data immediately while fetching fresh data
  });

  const photos = useMemo(() => data?.petPhotos?.photos || [], [data?.petPhotos?.photos]);
  const hasMore = data?.petPhotos?.hasMore || false;

  const { validPhotos, isValidating } = usePhotoValidation(photos);
  // Only show full-page loading on initial load (no data yet or loading with no photos).
  // When fetching more, keep showing existing content.
  // Also show loading while validating if we don't have valid photos yet.
  // Or if we have photos but no valid photos (validation pending).
  const isInitialLoading = userLoading
    || (!data && photosLoading)
    || (photosLoading && photos.length === 0)
    || (isValidating && validPhotos.length === 0)
    || (photos.length > 0 && validPhotos.length === 0); // Photos exist but validation not complete
  const isFetchingMore = photosLoading && photos.length > 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    track('awo_tab_click', { awo: awoTabs[newValue]?.awoName });
    setActiveTab(newValue);
  };

  const handleLoadMore = () => {
    track('community_show_more_click', { active_tab: awoTabs[activeTab]?.awoName });
    fetchMore({
      variables: {
        awoId: awoIdForQuery ? parseInt(awoIdForQuery, 10) : null,
        careTypes: COMMUNITY_CARE_TYPES,
        offset: photos.length,
        limit: PHOTOS_PER_PAGE,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          petPhotos: {
            photos: [
              ...prev.petPhotos.photos,
              ...fetchMoreResult.petPhotos.photos,
            ],
            hasMore: fetchMoreResult.petPhotos.hasMore,
          },
        };
      },
    });
  };

  return (
    <>
      <Box sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">Community Moments</Typography>
          <Typography variant="body2">
            Photos shared by pet parents across the Petcademy community. Send us a
            {' '}
            <Link component={RouterLink} to="/hub/messages">message</Link>
            {' '}
            to have your pet featured.
          </Typography>
        </HubSectionContainer>
      </Box>

      <HubSectionContainer wide>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="community moments tabs"
            variant="scrollable"
            scrollButtons={false}
          >
            {awoTabs.map((tab, index) => (
              <Tab
                key={tab.awoId}
                label={tab.awoName}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>
      </HubSectionContainer>
      <Box sx={{ background: '#F6F5FA', pb: 4, flexGrow: 1 }}>
        <HubSectionContainer wide backgroundContrast sx={{ py: 3 }}>
          {isInitialLoading && <PhotoLoadingGrid />}

          {!isInitialLoading && !isValidating && photos.length === 0 && validPhotos.length === 0 && (
            <PhotoEmptyState
              caption="Your photo feed will start filling up as you share moments with your pet."
            />
          )}

          {!isInitialLoading && validPhotos.length > 0 && (
          <>
            <Grid container spacing={2}>
              {validPhotos.map((photo) => (
                <Grid item xs={12} sm={4} md={3} key={photo.id}>
                  <PhotoCard
                    photo={photo}
                    onImageClick={() => openMediaDialog(photo.url, photo.createdAt, photoPetLabel(photo))}
                  />
                </Grid>
                  ))}
            </Grid>

            {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="text"
                onClick={handleLoadMore}
                disabled={isFetchingMore}
              >
                {isFetchingMore ? 'Loading...' : 'Show More'}
              </Button>
            </Box>
                )}
          </>
            )}
        </HubSectionContainer>
      </Box>
      <MediaDialog
        dialogImg={dialogState.img}
        dialogDate={dialogState.date}
        imgDialogOpen={dialogState.open}
        setImgDialogOpen={setDialogOpen}
        petName={dialogState.petName}
      />
    </>
  );
}

export default CommunityMoments;
